// ═══════════════════════════════════════════════════════════════════════════
// feedUi.js — pure helpers for the integration feed screens
// ═══════════════════════════════════════════════════════════════════════════
// No React, no network, no storage. Shared by pages/AdminFeed.js and the
// Feed* components.
// • Code → i18n key for errors, warnings, statuses and Daily Log sources. The
//   API answers with codes (docs/integrations/feed-api-contract.md, plus the
//   .NET host's own, docs/integrations/FEED_ALIGN_STATUS.md section 1), never
//   sentences; a code this file doesn't know maps to a generic key, so a new
//   server code never reaches the screen as a raw catalog key.
// • Platform notes: what each UrbanPiper platform doesn't send (plan 7.3,
//   appendix 7 "Providers > UrbanPiper" and "Mapping rules", with the
//   appendix 6 corrections), plus the preset channel name to SUGGEST. A
//   suggestion is never applied for the user, and a channel is never created.
// • Validators and formatters the setup screen shares.
// ═══════════════════════════════════════════════════════════════════════════

const has = (o, k) => Object.prototype.hasOwnProperty.call(o, k);
const CODE_RE = /^[a-z][a-z0-9_]{0,63}$/;

// ── Error codes: contract section 3, each route's extra codes in section 5,
//    and the client's own codes in section 7 (lib/feedApi.js). ───────────────
export const FEED_ERROR_CODES = Object.freeze([
  // section 3
  "invalid_request", "unauthorized", "forbidden", "feed_not_enabled", "not_paid", "not_found",
  "connection_disconnected", "payload_too_large", "rate_limited", "internal_error", "unavailable",
  // 5.3 create
  "unsupported_provider", "invalid_biz_id", "consent_required", "connection_exists", "biz_id_claimed",
  // 5.8 mappings
  "unknown_store", "workspace_not_in_account", "workspace_archived", "channel_not_in_workspace",
  "invalid_time_zone", "invalid_cutoff", "invalid_platform_code", "time_zone_required",
  "channel_already_fed", "channel_has_other_code", "channel_has_fed_days", "feed_from_after_fed_days",
  "workspace_is_fed",
  // 5.9 conflicts, 5.16 rebuild
  "invalid_range", "range_too_long",
  // 5.10 day choices
  "too_many_choices", "day_not_fed",
  // 5.11 import
  "too_many_rows",
  // 5.12 pause, 5.13 resume
  "invalid_status",
  // 5.17 live stores
  "invalid_month",
  // The .NET host (FEED_ALIGN_STATUS.md section 1): the import's whole-request
  // refusals (items 6, 20 and 25), and a feed window that would cover a
  // Platform contribution entry (mappings, resume, a widening file: items 21
  // and 28).
  "not_approved", "connection_paused", "unreadable_report", "preview_stale", "too_many_days",
  "channel_has_cofunding",
  // The .NET foundation's authentication, after the merge (503).
  "auth_unavailable", "db_unavailable",
  // section 7, client only
  "not_signed_in", "network_error", "timeout", "bad_response", "unexpected_response",
]);
const ERROR_SET = new Set(FEED_ERROR_CODES);

// The snake_case code of a FeedApiError (duck-typed on { error }, so a test
// double or a structured clone works too), or null for anything else.
export function feedErrorCode(err) {
  const code = err && typeof err === "object" ? err.error : null;
  return typeof code === "string" && CODE_RE.test(code) ? code : null;
}
export function isKnownFeedError(err) {
  return ERROR_SET.has(feedErrorCode(err));
}
export function feedErrorKey(err) {
  const code = feedErrorCode(err);
  return ERROR_SET.has(code) ? `feed.err.${code}` : "feed.err.unknown";
}

// ── Warning codes (contract 4.2, and the host's cofund_on_fed_channel) ──────
// No code for typed days: the feed replaces them by default (rows kept for
// restore), so there is nothing to wait for.
export const FEED_WARNING_CODES = Object.freeze([
  "unmapped_store", "unmapped_platform", "time_zone_missing", "not_approved", "access_lost",
  "apply_errors", "fully_discounted_orders", "no_status_updates", "open_orders", "quiet_store",
  "cofund_on_fed_channel",
]);
const WARNING_SET = new Set(FEED_WARNING_CODES);
export function warningKey(code) {
  return WARNING_SET.has(code) ? `feed.warn.${code}` : "feed.warn.unknown";
}
// Most of these need action; the rest are informational. A Platform
// contribution entry on fed days still changes today's figures (co-funding
// stays off on fed rows), so it is red too.
const URGENT_WARNINGS = new Set(["access_lost", "apply_errors", "time_zone_missing", "cofund_on_fed_channel"]);
export function warningTone(code) {
  if (URGENT_WARNINGS.has(code)) return "red";
  return WARNING_SET.has(code) ? "amber" : "grey";
}

// ── History import: why a row was left out or refused (contract 5.11) ───────
// The server's per-row rejection reasons (the contract's five, then the .NET
// host's, FEED_ALIGN_STATUS.md section 1 item 5), plus the upload screen's own
// "scientific_id" (a spreadsheet app rewrote a long ID as 1.23E+18) and
// "cell_count_mismatch" (a CSV line that doesn't line up with the header).
export const IMPORT_REJECT_REASONS = Object.freeze([
  "missing_order_id", "missing_store_id", "invalid_created_at", "duplicate_in_request", "ref_mismatch",
  "missing_platform_code", "missing_subtotal", "live_day_not_in_feed", "location_not_ready", "malformed",
  "refused_by_inbox", "invalid_order_id", "invalid_store_id", "invalid_brand_id", "invalid_discount", "unreadable_row",
]);
const IMPORT_REASON_SET = new Set([...IMPORT_REJECT_REASONS, "scientific_id", "cell_count_mismatch"]);
export function importReasonKey(code) {
  return IMPORT_REASON_SET.has(code) ? `feed.import.reason.${code}` : "feed.import.reason.unknown";
}

// ── Connection statuses (contract 4.1) ──────────────────────────────────────
export const FEED_STATUSES = Object.freeze(["pending", "live", "paused", "disconnected"]);
const STATUS_SET = new Set(FEED_STATUSES);
export function statusKey(status) {
  return STATUS_SET.has(status) ? `feed.status.${status}` : "feed.status.unknown";
}

// ── Daily Log sources a typed row can have (contract 4.6 typed.sources) ─────
const SOURCES = new Set(["manual", "scrape", "statement"]);
export function sourceKey(source) {
  return SOURCES.has(source) ? `feed.source.${source}` : null;
}

// ── Providers: proper nouns, shown as data. ─────────────────────────────────
const PROVIDER_NAMES = Object.freeze({ urbanpiper: "UrbanPiper", grubtech: "Grubtech", deliverect: "Deliverect" });
export function providerName(provider) {
  return typeof provider === "string" && has(PROVIDER_NAMES, provider) ? PROVIDER_NAMES[provider] : (provider || "—");
}

// ── Platforms (UrbanPiper details.channel codes, lower-case) ────────────────
// channel: the preset channel name to suggest (backend/lib/platformDefaults.cjs,
// pages/AdminSubscriptions.js MARKETS). notes: what the platform doesn't send.
// The jahez and hungerstation codes are unconfirmed (contract open question
// 3); a code that doesn't match falls to the generic "unverified" note.
const PLATFORMS = Object.freeze({
  deliveroo:     { channel: "Deliveroo",   notes: ["funderUnknown", "acceptanceTime", "posCancel"] },
  justeat:       { channel: "JustEat",     notes: ["funderUnknown", "acceptanceTime", "posCancel"] },
  ubereats:      { channel: "Uber Eats",   notes: ["funderUnknown", "acceptanceTime"] },
  talabat:       { channel: "Talabat",     notes: ["funderUnknown"] },
  noonfood:      { channel: "Noon Food",   notes: ["funderUnknown"] },
  careem:        { channel: "Careem Food", notes: ["deliveryFeeDiscount"] },
  keeta:         { channel: "Keeta",       notes: ["deliveryFeeDiscount"] },
  zomato:        { channel: "Zomato",      notes: ["deliveryFeeDiscount", "zomatoRequest"] },
  swiggy:        { channel: "Swiggy",      notes: ["swiggyShare"] },
  hungerstation: { channel: null,          notes: ["acceptanceTime"] },
  jahez:         { channel: null,          notes: ["jahezCancel"] },
});
// The merchant's own ordering channels: parked as "not tracked" for the owner.
const OWN_CHANNELS = new Set(["web", "app_android", "app_ios", "satellite"]);

export function normPlatformCode(code) {
  return typeof code === "string" ? code.trim().toLowerCase() : "";
}

// { code, known, own, channelName, noteKeys }
export function platformInfo(code) {
  const c = normPlatformCode(code);
  if (OWN_CHANNELS.has(c)) {
    return { code: c, known: true, own: true, channelName: null, noteKeys: ["feed.note.ownChannel"] };
  }
  if (has(PLATFORMS, c)) {
    const p = PLATFORMS[c];
    return { code: c, known: true, own: false, channelName: p.channel, noteKeys: p.notes.map((n) => `feed.note.${n}`) };
  }
  return { code: c, known: false, own: false, channelName: null, noteKeys: ["feed.note.unknownPlatform"] };
}

const looseName = (s) => (typeof s === "string" ? s.toLowerCase().replace(/[^a-z0-9]/g, "") : "");

// The id of the location's existing channel whose name matches the platform's
// preset (or the code itself), or null. A suggestion only: never applied for
// the user. Own channels get no suggestion.
export function suggestChannelId(code, channels) {
  const info = platformInfo(code);
  if (info.own || !info.code) return null;
  const want = looseName(info.channelName || info.code);
  const hit = (Array.isArray(channels) ? channels : []).find((ch) => ch && looseName(ch.platform) === want);
  return hit ? hit.id : null;
}

// ── Ids and form fields ─────────────────────────────────────────────────────
const UUID_RE = /^[0-9a-f]{8}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{12}$/i;
export function isUuid(v) {
  return typeof v === "string" && UUID_RE.test(v.trim());
}
// The UrbanPiper Biz ID stays a string: leading zeros are kept.
export function isBizId(v) {
  return typeof v === "string" && /^\d{1,20}$/.test(v.trim());
}
export function isTermsVersion(v) {
  if (typeof v !== "string") return false;
  const s = v.trim();
  return s.length >= 1 && s.length <= 40;
}

// ── Time zones and the trading day cutoff ───────────────────────────────────
export function isValidTimeZone(tz) {
  if (typeof tz !== "string") return false;
  const s = tz.trim();
  if (!/^[A-Za-z][A-Za-z0-9_+\-/]{0,63}$/.test(s)) return false;
  try {
    const f = new Intl.DateTimeFormat("en-GB", { timeZone: s });
    return Boolean(f);
  } catch (_) {
    return false;
  }
}

let zoneList = null;
// IANA names for the time zone picker's suggestions ([] where unsupported).
export function timeZoneOptions() {
  if (zoneList) return zoneList;
  try {
    zoneList = typeof Intl.supportedValuesOf === "function" ? Intl.supportedValuesOf("timeZone").slice() : [];
  } catch (_) {
    zoneList = [];
  }
  return zoneList;
}

const ZONE_BY_CURRENCY = Object.freeze({ GBP: "Europe/London", AED: "Asia/Dubai", INR: "Asia/Kolkata" });
// A suggestion from the location's currency, never filled in for the user.
export function suggestedTimeZone(currency) {
  return typeof currency === "string" && has(ZONE_BY_CURRENCY, currency) ? ZONE_BY_CURRENCY[currency] : null;
}

// "HH:MM" from "HH:MM" or "HH:MM:SS"; "" for anything else (null = midnight).
export function hhmm(v) {
  return typeof v === "string" && /^([01]\d|2[0-3]):[0-5]\d/.test(v) ? v.slice(0, 5) : "";
}
// "" (midnight) or an "HH:MM" before 12:00 (046's CHECK).
export function isValidCutoff(v) {
  return v === "" || (typeof v === "string" && /^(0\d|1[01]):[0-5]\d$/.test(v));
}

// ── Local business days ("YYYY-MM-DD") ──────────────────────────────────────
// Parsed as UTC dates and never through new Date(ymd) in local time, so a
// day can't shift in a browser west of UTC.
export function parseDay(ymd) {
  if (typeof ymd !== "string" || !/^\d{4}-\d{2}-\d{2}$/.test(ymd)) return null;
  const [y, m, d] = ymd.split("-").map(Number);
  const ms = Date.UTC(y, m - 1, d);
  const back = new Date(ms);
  return back.getUTCFullYear() === y && back.getUTCMonth() === m - 1 && back.getUTCDate() === d ? ms : null;
}
export function isDay(ymd) {
  return parseDay(ymd) != null;
}
// Whole days from `from` to `to` (negative when reversed), or null.
export function daysBetween(from, to) {
  const a = parseDay(from);
  const b = parseDay(to);
  return a == null || b == null ? null : Math.round((b - a) / 86400000);
}

// ── Formatting (en-GB, like the other admin screens) ────────────────────────
export function fmtDay(ymd) {
  const ms = parseDay(ymd);
  if (ms == null) return typeof ymd === "string" && ymd ? ymd : "—";
  return new Date(ms).toLocaleDateString("en-GB", { weekday: "short", day: "2-digit", month: "short", year: "numeric", timeZone: "UTC" });
}
// Instants (UTC ISO) in the viewer's own time, mirroring App.js fmtDate/fmtDateTime.
export function fmtDate(iso) {
  if (!iso) return "—";
  const dt = new Date(iso);
  return isNaN(dt.getTime()) ? "—" : dt.toLocaleDateString("en-GB", { day: "2-digit", month: "short", year: "numeric" });
}
export function fmtDateTime(iso) {
  if (!iso) return "—";
  const dt = new Date(iso);
  return isNaN(dt.getTime())
    ? "—"
    : dt.toLocaleString("en-GB", { day: "2-digit", month: "short", year: "numeric", hour: "2-digit", minute: "2-digit" });
}
// Money in the given location's currency. lib/format.js fmt() follows the
// viewer's own workspace currency, which is wrong for another account's data.
export function fmtMoney(value, currency) {
  if (typeof value !== "number" || !Number.isFinite(value)) return "—";
  if (typeof currency === "string" && /^[A-Z]{3}$/.test(currency)) {
    try {
      return new Intl.NumberFormat("en-GB", { style: "currency", currency, minimumFractionDigits: 2, maximumFractionDigits: 2 }).format(value);
    } catch (_) {
      /* not a currency Intl knows: plain number below */
    }
  }
  return value.toLocaleString("en-GB", { minimumFractionDigits: 2, maximumFractionDigits: 2 });
}
